import { AppTooltip, Badge, Card, Text } from '@components'
import type { Setup } from '@here/api'
import { Triangle } from 'lucide-solid'
import { type ParentComponent, Show } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Divider, Flex, Grid } from 'styled-system/jsx'
import SlungBadge from '../../SlungBadge'
import { useSingle } from '../state'

interface HeaderProps {
	setup: Setup
}

const Hero: ParentComponent<HeaderProps> = (props) => {
	const { takenResource, takenAverageScoreResource, leaderScoreResource } =
		useService(useSingle)()

	const score = () => {
		const val = takenAverageScoreResource()
		if (val == null) return '-'
		return Number.isInteger(val) ? val : val.toFixed(1)
	}

	const entry = () =>
		props.setup.position === 'long' ? props.setup.high_3_price : props.setup.low_3_price
	const stop = () =>
		props.setup.position === 'long' ? props.setup.low_3_price : props.setup.high_3_price

	return (
		<>
			<Card.Root
				p={4}
				bg="bg.emphasized"
			>
				<Grid
					gridTemplateColumns={{
            base: '1fr 1fr 1fr 1fr',
            lgDown: '1fr 1fr',
          }}
					justifyItems="center"
				>
					<Grid gridTemplateColumns="auto auto">
						<Text
							size="xs"
							fontWeight="bold"
						>
							JiT Entry:
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
              textAlign='right'
						>
							{entry()}
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
						>
							Stop Loss:
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
              textAlign='right'
						>
							{stop()}
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
						>
							RRR:
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
              textAlign='right'
						>
							{props.setup?.risk_reward_ratio}
						</Text>
					</Grid>

					<Grid gridTemplateColumns="auto auto">
						<Text
							size="xs"
							fontWeight="bold"
						>
							1st Interim:
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
              textAlign='right'
						>
							{props.setup?.interim_1}
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
						>
							2nd Interim:
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
              textAlign='right'
						>
							{props.setup?.interim_2}
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
						>
							Target:
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
              textAlign='right'
						>
							{props.setup?.target}
						</Text>
					</Grid>

					<Grid gridTemplateColumns="auto auto">
						<Text
							size="xs"
							fontWeight="bold"
						>
							Community Score:
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
              textAlign='right'
						>
							{takenResource()?.score || '-'}
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
						>
							My Score:
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
              textAlign='right'
						>
							{score()}
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
						>
							PHQ Score:
						</Text>
						<Text
							size="xs"
							fontWeight="bold"
              textAlign='right'
						>
							{leaderScoreResource() || '-'}
						</Text>
					</Grid>

					<Grid gap={1}>
						<Grid gridTemplateColumns="auto 1fr">
							<SlungBadge patternSpecification={props.setup?.pattern_specification} />
							<Badge
								style={{
									background:
										props.setup.pattern_specification === 'perfectly-symmetrical'
											? '#0098ff'
											: `var(--colors-${props.setup?.pattern_specification})`,
								}}
								color="white"
								justifyContent="center"
							>
								{props.setup?.pattern_specification.replaceAll('-', ' ')}
							</Badge>
						</Grid>
						<Show when={props.setup?.has_primer}>
							<Grid gridTemplateColumns="auto 1fr">
								<AppTooltip content="Has Primer">
									<Triangle
										class={css({
											transform: 'rotateZ(90deg)',
										})}
										fill="#6F349F"
										stroke="#6F349F"
									/>
								</AppTooltip>
								<Badge
									color="white"
									bg="#6F349F"
									justifyContent="center"
								>
									Has Primer
								</Badge>
							</Grid>
						</Show>
						<Show when={props.setup?.first_in_new_trend}>
							<Grid gridTemplateColumns="auto 1fr">
								<AppTooltip content="potential overperformance">
									<Triangle
										fill="#00A881"
										stroke="#00A881"
									/>
								</AppTooltip>
								<Badge
									color="white"
									bg="#00A881"
									justifyContent="center"
								>
									Potential Overperformance
								</Badge>
							</Grid>
						</Show>
					</Grid>

					<Divider
						gridColumn={{
            base: '1 / 5',
            lgDown: '1 / 3',
          }}
						borderColor="bg.default"
					/>

					<Flex gap={1}>
						<Text
							size="xs"
							fontWeight="bold"
						>
							Stage:
						</Text>
						<Text size="xs">{props.setup.stage}</Text>
					</Flex>

					<Flex gap={1}>
						<Text
							size="xs"
							fontWeight="bold"
						>
							Position:
						</Text>
						<Text size="xs">{props.setup.position}</Text>
					</Flex>

					<Flex gap={1}>
						<Text
							size="xs"
							fontWeight="bold"
						>
							Duration:
						</Text>
						<Text size="xs">{props.setup.duration}</Text>
					</Flex>

					<Flex gap={1}>
						<Text
							size="xs"
							fontWeight="bold"
						>
							Exchange:
						</Text>
						<Text size="xs">{props.setup.exchange}</Text>
					</Flex>
				</Grid>
			</Card.Root>

		</>
	)
}

export default Hero
